import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  instagramConnectionService,
  InstagramConnection,
  DemographicsData,
} from '../services/instagramConnectionService';

interface InstagramConnectionState {
  // State
  connections: InstagramConnection[];
  demographics: DemographicsData | null;
  loading: boolean;
  demographicsLoading: boolean;
  error: string | null;

  // Actions
  loadConnections: (organizationId: string) => Promise<void>;
  startConnection: (organizationId: string, customerId: string, platformAccountId: string, partitionKey: number, fbPageId?: string) => Promise<void>;
  exchangeCode: (code: string, state: string, organizationId: string) => Promise<boolean>;
  disconnect: (oauthAccountId: string, organizationId: string) => Promise<void>;
  loadDemographics: (customerId: string, platformAccountId: string, partitionKey: number) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

const useInstagramConnectionStore = create<InstagramConnectionState>()(
  devtools(
    (set, get) => ({
      connections: [],
      demographics: null,
      loading: false,
      demographicsLoading: false,
      error: null,

      loadConnections: async (organizationId: string) => {
        if (get().loading) return;
        set({ loading: true, error: null });

        try {
          const response = await instagramConnectionService.getConnectionStatus(organizationId);
          if (response.success) {
            set({ connections: response.data || [], loading: false });
          } else {
            set({ error: response.error?.message || 'Failed to load connections', loading: false });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load connections',
            loading: false,
          });
        }
      },

      startConnection: async (organizationId: string, customerId: string, platformAccountId: string, partitionKey: number, fbPageId?: string) => {
        set({ error: null });

        try {
          const response = await instagramConnectionService.getAuthorizationUrl(organizationId, customerId, platformAccountId, partitionKey, fbPageId);
          if (response.success && response.data.authorizeUrl) {
            // Redirect to Facebook OAuth
            window.location.href = response.data.authorizeUrl;
          } else {
            set({ error: response.error?.message || 'Failed to get authorization URL' });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to start connection',
          });
        }
      },

      exchangeCode: async (code: string, state: string, organizationId: string) => {
        set({ error: null });

        try {
          const response = await instagramConnectionService.exchangeCode(code, state);
          if (response.success) {
            await get().loadConnections(organizationId);
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to complete connection' });
            return false;
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to complete connection',
          });
          return false;
        }
      },

      disconnect: async (oauthAccountId: string, organizationId: string) => {
        set({ error: null });

        try {
          const response = await instagramConnectionService.disconnect(oauthAccountId);
          if (response.success) {
            // Reload connections to reflect the change
            await get().loadConnections(organizationId);
          } else {
            set({ error: response.error?.message || 'Failed to disconnect' });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to disconnect',
          });
        }
      },

      loadDemographics: async (customerId: string, platformAccountId: string, partitionKey: number) => {
        set({ demographicsLoading: true, error: null });

        try {
          const response = await instagramConnectionService.getDemographics(
            customerId,
            platformAccountId,
            partitionKey
          );
          if (response.success && response.data.hasData) {
            set({ demographics: response.data.demographics, demographicsLoading: false });
          } else {
            set({ demographics: null, demographicsLoading: false });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load demographics',
            demographicsLoading: false,
          });
        }
      },

      clearError: () => set({ error: null }),

      reset: () => set({
        connections: [],
        demographics: null,
        loading: false,
        demographicsLoading: false,
        error: null,
      }),
    }),
    { name: 'instagram-connection-store' }
  )
);

export default useInstagramConnectionStore;
